<script setup lang="ts">
  import type { AgencyGroup } from '@gat-ship/types'
  import { computed, ref } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { inject } from 'vue'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { IconArchive, IconLoading } from '@gat-ship/icons'

  const agencyStructureProvider = inject(agencyStructureKey)

  /** The open-state of the dialog. */
  const isOpen = ref(false)

  const isLoading = ref(false)

  const agencyGroup = ref<AgencyGroup | undefined>()

  const groupName = computed(() => agencyGroup.value?.name)

  function closeDialog() {
    agencyGroup.value = undefined
    isLoading.value = false
    isOpen.value = false
  }

  const canDelete = ref(false)

  async function onDeactivateAgencyClick() {
    if (!agencyGroup.value) {
      throw new Error('No agency found')
    }
    if (!canDelete.value) {
      return
    }
    const agencyId = agencyGroup.value.id
    isLoading.value = true
    try {
      await agencyStructureProvider?.commands.removeGroup(agencyId)
    } finally {
      closeDialog()
    }
  }

  export interface ShowRemoveAgencyGroupModalParams {
    agencyGroup: AgencyGroup
    canDelete: boolean
  }

  function showModal(params: ShowRemoveAgencyGroupModalParams) {
    isOpen.value = true
    agencyGroup.value = params.agencyGroup
    canDelete.value = params.canDelete
  }

  defineExpose({
    showModal
  })
</script>
<template>
  <Modal v-model:open="isOpen" type="dialog" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconArchive class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Remove Group
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <div class="text-sm">
          You’re about to start the process of deleting
          <span class="font-semibold">{{ groupName ?? 'X' }}</span
          >.
        </div>
        <div v-if="canDelete" class="font-regular text-sm">
          This group will be permanently deleted and cannot be recovered.
        </div>
        <div v-if="!canDelete" class="text-base font-semibold">
          What you must do first
        </div>
        <div v-if="!canDelete" class="font-regular text-sm">
          This group cannot be removed yet beacuse it has one or more agencies
          connected to it. Please move or deactivate these agencies and try
          again.
        </div>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button
        :data-testid="testIds.common.dialog.saveButton"
        :disabled="!canDelete || isLoading"
        @click="onDeactivateAgencyClick"
      >
        <IconLoading v-if="isLoading" class="size-4" />
        Remove Group
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
